import { updateCompanyDetails, getCompanyDetails } from '@api/company';
import { UpdateCompanyDetailsRequest } from '@interfaces/models';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Validators } from '@helpers/constant';
import { useUserStore } from '@shared/stores';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { z } from 'zod';

const addressFormSchema = (t: TFunction) =>
  z.object({
    street: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(1, t('FORMS.REQUIRED'))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    buildingNumber: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(1, t('FORMS.REQUIRED'))
      .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 })),
    apartmentNumber: z
      .string()
      .trim()
      .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 }))
      .optional(),
    zipCode: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .regex(Validators.ZIP_CODE, t('FORMS.INVALID_ZIP_CODE')),
    city: z
      .string()
      .trim()
      .min(1, t('FORMS.REQUIRED'))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
  });

const companyAccountFormSchema = (t: TFunction) =>
  z.object({
    companyName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(1, t('FORMS.REQUIRED'))
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(150, t('FORMS.MAX_CHARACTERS', { limit: 150 })),
    nip: z.string(),
    primaryAddress: addressFormSchema(t),
    correspondenceAddress: addressFormSchema(t).nullish(),
  });

const COMPANY_ACCOUNT_DEFAULT_VALUES: z.infer<ReturnType<typeof companyAccountFormSchema>> = {
  companyName: '',
  nip: '',
  primaryAddress: {
    apartmentNumber: '',
    buildingNumber: '',
    zipCode: '',
    street: '',
    city: '',
  },
  correspondenceAddress: null,
};

export const useCompanyAccountManagement = (
  companyId: string,
  { onSuccess }: { onSuccess?: () => void } = { onSuccess: () => {} },
) => {
  const { updateCompanyName: updateCompanyNameStore } = useUserStore();
  const { t } = useTranslation();

  const { data: companyData, isLoading: isCompanyDataLoading } = useQuery({
    queryKey: ['company-details', companyId],
    queryFn: () => getCompanyDetails(companyId),
    enabled: !!companyId,
  });

  const form = useForm({
    resolver: zodResolver(companyAccountFormSchema(t)),
    defaultValues: COMPANY_ACCOUNT_DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const { mutate: updateCompany, isPending: isUpdatePending } = useMutation({
    mutationFn: updateCompanyDetails,
    onSuccess: () => {
      onSuccess?.();
      updateCompanyNameStore(companyId, form.getValues().companyName);
      toast.success(t('COMPANY_ACCOUNT_MANAGEMENT.SUCCESS'));
    },
    onError: () => {
      toast.error(t('API_ERRORS.DEFAULT'));
    },
  });

  const correspondenceAddress = form.watch('correspondenceAddress');
  const isCorrespondenceAddressVisible = useMemo(
    () => !!correspondenceAddress,
    [correspondenceAddress],
  );

  const showCorrespondenceAddress = useCallback(() => {
    const value = form.getValues();
    form.setValue('correspondenceAddress', value.primaryAddress);
  }, [form]);

  const hideCorrespondenceAddress = useCallback(() => {
    form.setValue('correspondenceAddress', undefined);
  }, [form]);

  useEffect(() => {
    form.reset(companyData);
  }, [companyData, form]);

  const onSubmit = form.handleSubmit((data) => {
    // we get null from be if correspondenceAddress is not set but UpdateCompanyDetailsRequest expects undefined
    updateCompany({ id: companyId, data: data as UpdateCompanyDetailsRequest });
  });

  return {
    showCorrespondenceAddress,
    hideCorrespondenceAddress,
    isCorrespondenceAddressVisible,
    isCompanyDataLoading,
    isUpdatePending,
    onSubmit,
    form,
    t,
  };
};
